import React, { FC, use, useEffect, useState } from "react";
import { ColDef, IHeaderParams } from "ag-grid-community";
import { FormattedMessage, useIntl } from "react-intl";
import { Flex, Space, Tooltip } from "antd";
import { Button } from "@heroui/react";
import { FilterTwoTone, SearchOutlined } from "@ant-design/icons";
import { DmsTableCols, TableRowColors, TranslateDmsTableColumns } from "@dms/modules/DocumentTableModule/consts";
import { GQL } from "@binale-tech/shared";
import styles from "@dms/modules/DocumentTableModule/DocumentTable.module.scss";
import { SettingsColumns } from "@dms/modules/DocumentTableModule/components/SettingsColumns/SettingsColumns";
import { tableModeEnum } from "@dms/types";
import { TableActionsCellProps } from "@dms/modules/DocumentTableModule/components/CellRendererTableActionsList/CellRendererTableActionList";
import { DmsAppContext } from "@dms/types/ContextTypes";
import { useDocumentTableConfig } from "@dms/modules/DocumentTableModule/hooks/useDocumentColumnConfig";

export type TableActionsHeaderProps = {
    mode: tableModeEnum;
};

interface ColumnDefWithFilter extends ColDef {
    floatingFilter?: boolean;
}

export const HeaderComponentActions: FC<IHeaderParams<GQL.IDocument> & TableActionsCellProps> = props => {
    const { showColumnMenu, api } = props;
    const intl = useIntl();
    const [openSearch, setOpenSearch] = useState(false);
    const { activeType } = use(DmsAppContext);
    const { enabledColumns } = useDocumentTableConfig(props.mode, activeType);

    const [color, setColor] = useState<string>("");
    const [tooltipText, setTooltipText] = useState(intl.formatMessage({ id: "app.dms.filterColor" }));

    useEffect(() => {
        const handleFilterChanged = () => {
            const newModel = api.getFilterModel().actions;

            if (!newModel) {
                setTooltipText("Color filter");
                setColor("");
                return;
            }

            const color = newModel as string;
            setTooltipText(
                `${intl.formatMessage({ id: "app.dms.filterColor" })}: ${intl.formatMessage({ id: `app.button.color.${color}` })}`
            );
            setColor(color);
        };

        api.addEventListener("filterChanged", handleFilterChanged);

        if (api.isDestroyed()) {
            api.removeEventListener("filterChanged", handleFilterChanged);
        }
    }, [api]);

    const toggleFloatingFilter = () => {
        api.setFilterModel({});

        // api.updateGridOptions({ columnDefs: newColModel });
        setOpenSearch(prev => {
            const floatingFilter = !prev;
            const columnDefs = api.getColumnDefs() as ColumnDefWithFilter[];
            api.updateGridOptions({
                columnDefs: columnDefs.map(col => ({ ...col, floatingFilter })),
            });
            return floatingFilter;
        });
    };

    if (props.mode !== tableModeEnum.app) {
        return null;
    }

    return (
        <Flex align="center" style={{ width: "100%", justifyContent: "space-between" }}>
            <FormattedMessage tagName="span" id={TranslateDmsTableColumns[DmsTableCols.ACTIONS]} />
            <Space>
                <Button
                    radius={"full"}
                    variant={"light"}
                    color={"default"}
                    size={"sm"}
                    isIconOnly
                    onPress={toggleFloatingFilter}
                    style={{ fontSize: 14 }}
                >
                    <SearchOutlined />
                </Button>

                <Tooltip title={tooltipText}>
                    <Button
                        radius={"full"}
                        variant={"light"}
                        color={"default"}
                        size={"sm"}
                        isIconOnly
                        onPress={e => showColumnMenu(e.target as any)}
                        style={{ fontSize: 14 }}
                    >
                        {color ? (
                            <FilterTwoTone
                                twoToneColor={[
                                    color === GQL.IDocumentColors.Yellow ? "SandyBrown" : color,
                                    TableRowColors[color as GQL.IDocumentColors],
                                ]}
                            />
                        ) : (
                            <FilterTwoTone twoToneColor={["rgba(0, 0, 0, 0.85)", "transparent"]} />
                        )}
                    </Button>
                </Tooltip>

                <SettingsColumns
                    activeType={activeType}
                    enabledColumns={enabledColumns}
                    className={styles.columnSetting}
                />
            </Space>
        </Flex>
    );
};
